/*
  © 2024 Jhango — offcanvas cart drawer (ported from supplement theme).
  Talks Shopify bundled-section rendering: 'offcanvas-cart' + 'cart-icon-bubble'.
  Also defines a Dawn-compatible <cart-drawer> bridge so Dawn's product-form.js
  (getSectionsToRender / renderContents / setActiveElement) opens this drawer.
*/

/* Dawn defines window.routes (not window.Shopify.routes like the supplement
   theme) — resolve with literal fallbacks so cart calls never hit undefined. */
const csfRoutes = {
  add: (window.routes && window.routes.cart_add_url) || '/cart/add',
  change: (window.routes && window.routes.cart_change_url) || '/cart/change',
  root: (window.Shopify && window.Shopify.routes && (window.Shopify.routes.root || window.Shopify.routes.root_url)) || '/'
}

class Cart extends HTMLElement {
  constructor () {
    super()

    /* Delegated bindings — several theme scripts (jg-product-card,
       shoppable-vids) refresh this drawer by swapping innerHTML, which would
       silently drop per-node listeners. Delegation survives every refresh. */
    this.addEventListener('change', event => {
      const input = event.target.closest('[name="updates[]"]')
      if (input) {
        // Debounce per line: quick taps on +/- send one request with the final quantity instead of
        // racing requests while the drawer is being re-rendered.
        const key = input.dataset.lineItemKey
        this.qtyTimers = this.qtyTimers || {}
        clearTimeout(this.qtyTimers[key])
        this.qtyTimers[key] = setTimeout(() => this.change(key, Number(input.value)), 350)
        return
      }
      const select = event.target.closest('.select-line-item-change')
      if (select) {
        this.changeVariant(select.dataset.lineItemKey, Number(select.value), Number(select.dataset.qty))
      }
    })

    this.addEventListener('click', event => {
      const btn = event.target.closest('.btn-line-item-remove')
      if (btn) {
        this.change(btn.dataset.lineItemKey, 0)
        return
      }
      const collapseToggle = event.target.closest('.collapse-wrapper [data-bs-toggle="collapse"]')
      if (collapseToggle) {
        setTimeout(() => {
          const offcanvasBody = this.querySelector('.offcanvas-body')
          if (offcanvasBody) offcanvasBody.scroll({ top: offcanvasBody.scrollHeight, behavior: 'smooth' })
        }, 250)
      }
    })

    this.showOffcanvasIfUrl()
  }

  static get sectionIds () {
    return ['offcanvas-cart', 'cart-icon-bubble', 'cart-count-badge']
  }

  openDrawer () {
    const offcanvas = document.querySelector('#offcanvas-cart')
    if (offcanvas && window.bootstrap) {
      document.querySelectorAll('.offcanvas.show').forEach(other => {
        if (other !== offcanvas) window.bootstrap.Offcanvas.getOrCreateInstance(other).hide()
      })
      window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show()
    }
  }

  async add (formData) {
    let response

    if (formData instanceof FormData) {
      formData.append('sections', Cart.sectionIds)
      formData.append('sections_url', window.location.pathname)

      response = await fetch(`${csfRoutes.add}.js`, {
        method: 'POST',
        body: formData
      })
    } else {
      formData.sections = Cart.sectionIds
      formData.sections_url = window.location.pathname

      response = await fetch(`${csfRoutes.add}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
    }

    const data = await response.json().catch(() => ({}))

    if (response.ok) {
      // Added fine — only re-render; a reply without sections still means success.
      window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }))
      if (data.sections) this.reloadCartElements(data.sections)
      else await this.resync()
    } else {
      await this.resync()
      this.showError(this.describeError(data, response, 'Could not add this to your cart. Please try again.'))
    }

    this.openDrawer()
  }

  async change (id, quantity) {
    this.classList.add('loading')

    try {
      const response = await fetch(`${csfRoutes.change}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id,
          quantity,
          sections: Cart.sectionIds,
          sections_url: window.location.pathname
        })
      })
      // Error responses (e.g. 429) can be HTML, so don't assume JSON.
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.sections) {
        window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: data }))
        this.reloadCartElements(data.sections)
        return
      }
      await this.resync()
      this.showError(this.describeError(data, response, 'Could not update the quantity. Please try again.'))
    } catch (error) {
      await this.resync()
      this.showError('Could not reach the store. Check your connection and try again.')
    } finally {
      // Previously skipped when the request threw, leaving the drawer faded out (".loading" = 20% opacity).
      this.classList.remove('loading')
      document.querySelectorAll('cart-container.loading').forEach(el => el.classList.remove('loading'))
    }
  }

  /* Apply a discount code to the real cart session, then re-render so the drawer shows the amounts
     Shopify actually calculated (conditions, caps and exclusions included) — never an estimate. */
  async applyDiscountCode (code) {
    const clean = String(code || '').trim()
    if (!clean) return { applied: false }

    this.classList.add('loading')
    try {
      await fetch(`${csfRoutes.root}discount/${encodeURIComponent(clean)}`, { credentials: 'same-origin' })
      await this.resync()

      const cart = await fetch(`${csfRoutes.root}cart.js`).then(r => r.json()).catch(() => null)
      const entry = cart && (cart.discount_codes || []).find(d => String(d.code).toLowerCase() === clean.toLowerCase())
      const applied = !!(entry && entry.applicable)

      if (!applied) this.showError(`Code "${clean}" is not valid for this cart.`)
      window.dispatchEvent(new CustomEvent('kt.cart.discount', { detail: { code: clean, applied } }))
      return { applied }
    } catch (error) {
      this.showError('Could not apply that code. Please try again.')
      return { applied: false }
    } finally {
      this.classList.remove('loading')
      document.querySelectorAll('cart-container.loading').forEach(el => el.classList.remove('loading'))
    }
  }

  // Re-render the drawer from the real cart so the quantity boxes never show numbers the cart didn't accept.
  async resync () {
    try {
      const response = await fetch(`${csfRoutes.root}?sections=${Cart.sectionIds.join(',')}`)
      if (response.ok) this.reloadCartElements(await response.json())
    } catch (error) {}
  }

  async changeVariant (lineItemKey, newVariantId, quantity) {
    this.classList.add('loading')
    try {

    const changeResponse = await fetch(`${csfRoutes.change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: 0,
        sections: Cart.sectionIds,
        sections_url: window.location.pathname
      })
    })
    const changeData = await changeResponse.json().catch(() => ({}))

    if (changeResponse.ok) {
      window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: changeData }))
    } else {
      this.showError(this.describeError(changeData, changeResponse, 'Could not change this item. Please try again.'))
    }

    const addResponse = await fetch(`${csfRoutes.add}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: newVariantId, quantity }],
        sections: Cart.sectionIds,
        sections_url: window.location.pathname
      })
    })
    const addData = await addResponse.json().catch(() => ({}))

    if (addResponse.ok && addData.sections) {
      window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: addData }))
      this.reloadCartElements(addData.sections)
    } else {
      await this.resync()
      this.showError(this.describeError(addData, addResponse, 'Could not change the variant. Please try again.'))
    }
    } catch (error) {
      await this.resync()
      this.showError('Could not reach the store. Check your connection and try again.')
    } finally {
      this.classList.remove('loading')
      document.querySelectorAll('cart-container.loading').forEach(el => el.classList.remove('loading'))
    }
  }

  reloadCartElements (sections) {
    if (!sections) return

    for (const [key, value] of Object.entries(sections)) {
      if (!value) continue
      const newDoc = new DOMParser().parseFromString(value, 'text/html')

      switch (key) {
        case 'offcanvas-cart': {
          const fresh = newDoc.querySelector('cart-container')
          if (fresh) {
            document.querySelectorAll('.cart-container').forEach(elem => {
              elem.replaceWith(fresh.cloneNode(true))
            })
          }
          break
        }
        case 'cart-icon-bubble': {
          const bubble = document.getElementById('cart-icon-bubble')
          const inner = newDoc.querySelector('.shopify-section')
          if (bubble && inner) bubble.innerHTML = inner.innerHTML
          break
        }
        case 'cart-count-badge': {
          // update text only — header badges carry their own extra classes
          const freshBadge = newDoc.querySelector('.cart-count-badge')
          if (freshBadge) {
            const count = freshBadge.textContent.trim()
            document.querySelectorAll('.cart-count-badge').forEach(elem => {
              elem.textContent = count
              elem.setAttribute('data-count', count)
            })
          }
          break
        }
      }
    }

    window.dispatchEvent(new CustomEvent('kt.cart.reloaded'))
  }

  /* Shopify's raw replies are developer text ("Invalid value for parameter 'id'", "Cart Error").
     Turn them into something a shopper can act on; keep genuinely useful messages (stock limits). */
  describeError (data, response, fallback) {
    const raw = String((data && (data.description || data.message)) || '').trim()
    const status = response && response.status

    if (status === 429) return 'The store is busy right now — please wait a few seconds and try again.'
    if (status === 404) return 'That item is no longer available. Your cart has been refreshed.'
    if (/invalid value for parameter|no valid id|missing parameter|not a valid|cart error|bad request|unexpected token|json|cannot find|can't find|not found|does not exist|no such|invalid/i.test(raw)) {
      return 'That item is no longer available in the cart. Your cart has been refreshed — please try again.'
    }
    if (/sold out|out of stock|can't add|cannot add|only .* left|in your cart|available/i.test(raw)) return raw
    if (raw && raw.length < 200 && /\s/.test(raw)) return raw
    return fallback
  }

  showError (message) {
    // Use the live container: the drawer may have been re-rendered since this element was created.
    const root = this.isConnected ? this : (document.querySelector('cart-container') || this)
    const alert = root.querySelector('.alert')
    const msg = alert && alert.querySelector('[data-alert-msg]')
    if (!msg) return

    msg.textContent = message
    alert.removeAttribute('hidden')
    alert.scrollIntoView({ block: 'nearest', behavior: 'smooth' })

    clearTimeout(Cart.errorTimer)
    Cart.errorTimer = setTimeout(() => {
      document.querySelectorAll('cart-container .alert').forEach(el => el.setAttribute('hidden', 'hidden'))
    }, 8000)
  }

  showOffcanvasIfUrl () {
    if (new URLSearchParams(window.location.search).has('cart')) {
      this.openDrawer()
    }
  }

}
if (!customElements.get('cart-container')) customElements.define('cart-container', Cart)

/* Dawn bridge — product-form.js finds <cart-drawer>, requests our sections and
   hands the parsed response back; we re-render and slide the drawer open. */
class CartDrawerBridge extends HTMLElement {
  getSectionsToRender () {
    return Cart.sectionIds.map(id => ({ id }))
  }

  renderContents (parsedState) {
    const container = document.querySelector('cart-container')
    if (container && parsedState.sections) {
      container.reloadCartElements(parsedState.sections)
    }
    const fresh = document.querySelector('cart-container')
    if (fresh) fresh.openDrawer()
  }

  setActiveElement (element) {
    this.activeElement = element
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawerBridge)

class CartUpsells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('select[name="id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage

    if (variantImage.length) {
      select.closest('.cart-upsells-item').querySelector('.img-wrapper img').setAttribute('src', variantImage)
    }
  }
}
if (!customElements.get('cart-upsells')) customElements.define('cart-upsells', CartUpsells)

class CartGoal extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    this.animateProgressBar()
    this.initTooltips()
    this.initConfetti()
  }

  animateProgressBar () {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  initTooltips () {
    if (!window.bootstrap || !window.bootstrap.Tooltip) return
    this.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) =>
      window.bootstrap.Tooltip.getOrCreateInstance(el))
  }

  async initConfetti () {
    if (this.dataset.showConfetti === 'false') return

    const goalCompleted = Number(this.dataset.goalCompleted)
    const prevGoalCompleted = Number(localStorage.getItem('ks-cart-goal-completed') || 0)

    if (prevGoalCompleted !== goalCompleted) {
      localStorage.setItem('ks-cart-goal-completed', goalCompleted)
    }

    if (goalCompleted <= prevGoalCompleted) return

    // Fetch the confetti library only now that a goal was actually reached.
    if (!window.confetti && this.dataset.confettiSrc) {
      await new Promise((resolve) => {
        const script = document.createElement('script')
        script.src = this.dataset.confettiSrc
        script.onload = resolve
        script.onerror = resolve
        document.head.appendChild(script)
      })
    }
    if (!window.confetti) return

    const myCanvas = document.createElement('canvas')
    myCanvas.setAttribute('id', 'cart-goal-confetti-canvas')
    document.querySelector('#offcanvas-cart .offcanvas-body').insertAdjacentElement('afterbegin', myCanvas)

    const myConfetti = window.confetti.create(myCanvas)

    myConfetti({
      particleCount: 400,
      spread: 90
    })

    setTimeout(() => {
      myCanvas.remove()
    }, 4000)
  }
}
if (!customElements.get('cart-goal')) customElements.define('cart-goal', CartGoal)

class CartDiscount extends HTMLElement {
  constructor () {
    super()

    this.input = this.querySelector('input')
    this.btn = this.querySelector('button')
    this.btn.addEventListener('click', this.onSubmit.bind(this))
  }

  async onSubmit () {
    const container = document.querySelector('cart-container')
    if (!container) return

    this.btn.style.width = this.btn.clientWidth + 2 + 'px'
    this.btn.classList.add('loading')

    // Previously this jumped straight to /checkout, so shoppers never saw whether the code worked.
    await container.applyDiscountCode(this.input.value)

    this.btn.classList.remove('loading')
  }
}
if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount)

/* Qty pill inside the drawer (PDP defines the same element in ww-product.js;
   whichever loads first wins — the API subset used here is identical). */
class CsfQtySwitcher extends HTMLElement {
  constructor () {
    super()

    this.qtyInput = this.querySelector('input')
    this.minusBtn = this.querySelector('button.btn-qty-minus')
    this.plusBtn = this.querySelector('button.btn-qty-plus')

    this.qtyInput.addEventListener('change', () => this.validateButtons())
    this.minusBtn.addEventListener('click', () => this.step(-1))
    this.plusBtn.addEventListener('click', () => this.step(1))

    window.addEventListener('kt.product.variant.change.newDoc', (event) => this.onVariantChange(event))
  }

  step (delta) {
    this.qtyInput.value = Number(this.qtyInput.value) + delta
    this.validateButtons()
    // Must bubble: <cart-container> listens for quantity changes by delegation.
    this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  validateButtons () {
    const min = this.qtyInput.getAttribute('min')
    const max = this.qtyInput.getAttribute('max')
    if (min && Number(this.qtyInput.value) <= Number(min)) {
      this.minusBtn.disabled = true
      this.qtyInput.value = min
    } else {
      this.minusBtn.disabled = false
    }
    if (max && Number(this.qtyInput.value) >= Number(max)) {
      this.plusBtn.disabled = true
      this.qtyInput.value = max
    } else {
      this.plusBtn.disabled = false
    }
  }

  onVariantChange (event) {
    if (!this.closest('.product-block-atc')) return

    const newQtyInput = event.detail.querySelector('.product-block-atc qty-switcher input')
    if (!newQtyInput) return
    const max = newQtyInput.getAttribute('max')
    if (max) this.qtyInput.setAttribute('max', max)
    this.validateButtons()
  }
}
if (!customElements.get('qty-switcher')) customElements.define('qty-switcher', CsfQtySwitcher)

/* Drawer add-to-cart forms (free gift picker, upsell cards) — plain forms with
   data-csf-cart-add, delegated so they survive drawer re-renders. */
document.addEventListener('submit', event => {
  const form = event.target.closest('form[data-csf-cart-add]')
  if (!form) return
  event.preventDefault()

  const container = document.querySelector('cart-container')
  if (!container) return

  const btn = form.querySelector('[type="submit"]')
  if (btn) { btn.classList.add('loading'); btn.disabled = true }

  container.add(new FormData(form)).finally(() => {
    const freshBtn = document.querySelector('form[data-csf-cart-add] [type="submit"].loading')
    if (freshBtn) { freshBtn.classList.remove('loading'); freshBtn.disabled = false }
  })
})

/* Header cart icons open the drawer instead of navigating to /cart
   (#cart-icon-bubble = Dawn header, .vhh-icon--cart = csf-header). */
document.addEventListener('click', event => {
  const icon = event.target.closest('#cart-icon-bubble, .vhh-icon--cart')
  if (!icon) return
  const offcanvas = document.querySelector('#offcanvas-cart')
  if (!offcanvas || !window.bootstrap) return
  event.preventDefault()
  window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show()
})
